import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getApi } from '../../Services/apiService';
import { toast } from 'react-toastify';
import {
  getPropertyListingStatusLabel,
  getPropertyListingStatusSlug,
  isAvailablePropertyStatus,
} from '../../constants/propertyListingStatus';

const AVATAR_COLORS = ['orange', 'blue', 'purple', 'green', 'teal'];

const DashboardPropertyImage = ({ image, address }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const imageRef = useRef(null);

  useEffect(() => {
    setImageLoaded(false);
    setImageError(false);
    const img = imageRef.current;
    if (img?.complete && img.naturalWidth > 0) {
      setImageLoaded(true);
    }
  }, [image]);

  if (imageError || !image) {
    return <div className="dashboard-property-image-placeholder" />;
  }

  return (
    <div className="dashboard-property-image-container">
      {!imageLoaded && <div className="dashboard-property-image-shimmer" aria-hidden="true" />}
      <img
        ref={imageRef}
        src={image}
        alt={address}
        className={`dashboard-property-image${imageLoaded ? ' dashboard-property-image-loaded' : ' dashboard-property-image-loading'}`}
        onLoad={() => setImageLoaded(true)}
        onError={() => setImageError(true)}
      />
    </div>
  );
};

const DashboardLlcBadge = ({ llcName, llcLogo }) => {
  const [logoError, setLogoError] = useState(false);

  useEffect(() => {
    setLogoError(false);
  }, [llcLogo]);

  if (!llcName && !llcLogo) return null;

  return (
    <div className="dashboard-property-llc" title={llcName || undefined}>
      {llcLogo && !logoError ? (
        <img
          src={llcLogo}
          alt={llcName ? `${llcName} logo` : 'Business logo'}
          className="dashboard-property-llc-logo"
          onError={() => setLogoError(true)}
        />
      ) : (
        <span className="dashboard-property-llc-fallback" aria-hidden="true">
          {(llcName || '?').trim().charAt(0).toUpperCase()}
        </span>
      )}
      {llcName && <span className="dashboard-property-llc-name">{llcName}</span>}
    </div>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [screenings, setScreenings] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [screeningsLoading, setScreeningsLoading] = useState(false);
  const [stats, setStats] = useState([
    { label: 'Total Properties', value: '0', icon: '/assets/images/sidebarPropertyIcon.svg' },
    { label: 'Active Properties', value: '0', icon: '/assets/images/sidebarPropertyIcon.svg' },
    { label: 'Expired Properties', value: '0', icon: '/assets/images/sidebarPropertyIcon.svg' },
    { label: 'Total Screening', value: '0', icon: '/assets/images/sidebarPropertyIcon.svg' },
  ]);

  // Transform API response to dashboard format
  const transformProperty = (apiProperty) => {
    // Get first property image or use placeholder
    const firstImage = apiProperty.property_images && apiProperty.property_images.length > 0
      ? apiProperty.property_images[0].image_url
      : 'https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=400';
    
    // Format address for dashboard (uppercase format)
    const address = apiProperty.address || '';
    const unitNumber = apiProperty.unit_number ? `, ${apiProperty.unit_number.toUpperCase()}` : '';
    const fullAddress = `${address.toUpperCase()}${unitNumber}`;
    
    // Format city and state
    const cityState = apiProperty.city && apiProperty.state 
      ? `${apiProperty.city}, ${apiProperty.state}`
      : apiProperty.city || apiProperty.state || '';
    
    // Format price
    const price = apiProperty.monthly_rent 
      ? `$${apiProperty.monthly_rent.toLocaleString()}/mo`
      : 'N/A';
    
    // Use property_code from API, fallback to first 5 characters of ID if not available
    const code = apiProperty.property_code || (apiProperty.id ? apiProperty.id.substring(0, 5) : '');

    // Use applicants_count from API (may be string or number)
    const applicants = (() => {
      const count = apiProperty.applicants_count ?? apiProperty.applicantsCount;
      if (count === undefined || count === null) return 0;
      const num = typeof count === 'string' ? parseInt(count, 10) : Number(count);
      return Number.isFinite(num) ? num : 0;
    })();

    return {
      id: apiProperty.id,
      image: firstImage,
      address: fullAddress,
      code: code,
      city: cityState,
      price: price,
      applicants,
      status: getPropertyListingStatusLabel(apiProperty.status),
      statusSlug: getPropertyListingStatusSlug(apiProperty.status),
      statusRaw: apiProperty.status,
      llcName: apiProperty.llc_name || '',
      llcLogo: apiProperty.llc_logo || '',
    };
  };

  // Fetch properties from API
  const fetchProperties = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: '1',
        limit: '10', // Fetch first 10 properties for dashboard
      });
      
      const response = await getApi(`/property/my-properties?${params.toString()}`);
      
      if (response?.statusCode === 200 && response?.data) {
        const apiData = response.data;
        const transformedProperties = apiData.data.map(transformProperty);
        setProperties(transformedProperties);
        
        // Calculate stats from API response where possible, otherwise fall back to local calculations
        const totalProperties =
          apiData.total_properties ??
          apiData.totalProperties ??
          apiData.totalItems ??
          apiData.total_items ??
          apiData.property_counts?.total ??
          transformedProperties.length;

        const activeProperties =
          apiData.active_properties ??
          apiData.activeProperties ??
          apiData.property_counts?.active ??
          transformedProperties.filter((p) => isAvailablePropertyStatus(p.statusRaw)).length;

        const expiredProperties =
          apiData.expired_properties ??
          apiData.expiredProperties ??
          apiData.property_counts?.expired ??
          transformedProperties.filter((p) => !isAvailablePropertyStatus(p.statusRaw)).length;

        const totalScreening =
          apiData.total_screening ??
          apiData.totalScreening ??
          apiData.total_screenings ??
          apiData.totalScreenings ??
          apiData.dashboard_counts?.total_screening ??
          apiData.dashboard_counts?.total_screenings ??
          apiData.dashboard_counts?.screening ??
          apiData.dashboard_counts?.screenings ??
          apiData.screening_counts?.total ??
          0;
        
        setStats([
          { label: 'Total Properties', value: totalProperties.toString(), icon: '/assets/images/sidebarPropertyIcon.svg' },
          { label: 'Active Properties', value: activeProperties.toString(), icon: '/assets/images/sidebarPropertyIcon.svg' },
          { label: 'Expired Properties', value: expiredProperties.toString(), icon: '/assets/images/sidebarPropertyIcon.svg' },
          { label: 'Total Screening', value: totalScreening.toString(), icon: '/assets/images/sidebarPropertyIcon.svg' },
        ]);
      } else {
        toast.error(response?.message);
        setProperties([]);
      }
    } catch (error) {
      setProperties([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Grade badge colors: 1 = light green, 2 = light blue, 3 = light red, other = neutral
  const getGradeStyle = (grade) => {
    const g = grade != null ? String(grade).trim() : '';
    if (g === '1') return { backgroundColor: '#d4edda', color: '#155724' };
    if (g === '2') return { backgroundColor: '#ABF0FD', color: '#004085' };
    if (g === '3') return { backgroundColor: '#f8d7da', color: '#721c24' };
    return { backgroundColor: '#e9ecef', color: '#495057' };
  };

  // Transform screening API item to dashboard format
  const transformScreening = (item, index) => {
    const tenant = item.tenant || {};
    const property = item.property || {};
    const firstName = tenant.first_name || '';
    const lastName = tenant.last_name || '';
    const initials = [firstName, lastName].map((s) => (s && s[0]) || '').join('').toUpperCase() || '?';
    const name = [firstName, lastName].filter(Boolean).join(', ') || '—';
    const baseAddress = property.address || [property.city, property.state].filter(Boolean).join(', ') || '';
    const address = property.unit_number
      ? `${baseAddress}${baseAddress ? ', ' : ''}Unit ${property.unit_number}`.trim()
      : baseAddress || '—';
    const color = AVATAR_COLORS[index % AVATAR_COLORS.length];
    const grade = item.grade != null && item.grade !== '' ? String(item.grade) : '—';
    return {
      id: item.id,
      initials,
      name,
      address,
      grade,
      color,
    };
  };

  // Fetch screenings from API
  const fetchScreenings = async () => {
    setScreeningsLoading(true);
    try {
      const params = new URLSearchParams({ page: '1', limit: '10' });
      const response = await getApi(`/dashboard/screenings?${params.toString()}`);
      if (response?.statusCode === 200 && response?.data) {
        const { data: items = [], items: itemsAlt = [], totalItems } = response.data;
        const list = items.length ? items : itemsAlt;
        setScreenings(list.map((item, i) => transformScreening(item, i)));
        setStats((prev) =>
          prev.map((s) =>
            s.label === 'Total Screening'
              ? { ...s, value: String(totalItems ?? list.length) }
              : s
          )
        );
      } else {
        setScreenings([]);
        if (response?.message) toast.error(response.message);
      }
    } catch (error) {
      setScreenings([]);
    } finally {
      setScreeningsLoading(false);
    }
  };

  // Fetch properties and screenings on component mount
  useEffect(() => {
    fetchProperties();
    fetchScreenings();
  }, []);

  const handleStatClick = (label) => {
    let typeParam = null;
    if (label === 'Active Properties') {
      typeParam = 0;
    } else if (label === 'Expired Properties') {
      typeParam = 1;
    } else if (label === 'Total Screening') {
      typeParam = 2;
    }

    const basePath = '/landlord/dashboard/items';
    const targetUrl = typeParam === null ? basePath : `${basePath}?type=${typeParam}`;
    navigate(targetUrl);
  };

  return (
    <div className="dashboard-page">
      <div className="dashboard-stats">
        {stats.map((stat, index) => {
          const isIconPath = typeof stat.icon === 'string' && (stat.icon.startsWith('/') || stat.icon.includes('.svg') || stat.icon.includes('.png') || stat.icon.includes('.jpg'));
          
          return (
            <div
              key={index}
              className="dashboard-stat-card"
              role="button"
              tabIndex={0}
              onClick={() => handleStatClick(stat.label)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleStatClick(stat.label);
                }
              }}
            >
              <div className="dashboard-stat-header">
                <div className="dashboard-stat-icon">
                  {isIconPath ? (
                    <img 
                      src={window.getPublicUrl ? window.getPublicUrl(stat.icon) : stat.icon} 
                      alt={stat.label} 
                      className="dashboard-stat-icon-img"
                    />
                  ) : (
                    stat.icon
                  )}
                </div>
                <div className="dashboard-stat-label">{stat.label}</div>
              </div>
              <div className="dashboard-stat-value">{stat.value}</div>
            </div>
          );
        })}
      </div>

      <div className="dashboard-content">
        <div className="dashboard-section">
          <div className="dashboard-section-header">
            <h2 className="dashboard-section-title">Your properties</h2>
            {properties.length > 0 && (
              <Link to="/landlord/properties" className="dashboard-section-link">
                View all
              </Link>
            )}
          </div>
          <div className="dashboard-properties-list">
            {isLoading ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                Loading properties...
              </div>
            ) : properties.length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#666', minHeight: '80px' }}>
                No properties found
              </div>
            ) : (
              properties.slice(0, 3).map((property) => (
                <Link
                  key={property.id}
                  to={`/landlord/properties/${property.id}`}
                  className="dashboard-property-card"
                >
                  <DashboardPropertyImage image={property.image} address={property.address} />
                    <div className="dashboard-property-info">
                      <div className="dashboard-property-address">{property.address}</div>
                      <div className="dashboard-property-code">Code: {property.code}</div>
                      <DashboardLlcBadge llcName={property.llcName} llcLogo={property.llcLogo} />
                      <div className="dashboard-property-city">{property.city}</div>
                      <div className="dashboard-property-details">
                        <span className="dashboard-property-price">{property.price}</span>
                        <span className="dashboard-property-applicants">{property.applicants} applicants</span>
                      </div>
                    </div>
                    <div
                      className={`dashboard-property-status dashboard-property-status-${property.statusSlug}`}
                    >
                      {property.status}
                    </div>
                  </Link>
              ))
            )}
          </div>
        </div>

        <div className="dashboard-section">
          <div className="dashboard-section-header">
            <h2 className="dashboard-section-title">Screening</h2>
            <Link to="/landlord/dashboard/items?type=2" className="dashboard-section-link">
              View all
            </Link>
          </div>
          <div className="dashboard-screenings-list">
            {screeningsLoading ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                Loading screenings...
              </div>
            ) : screenings.length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#666', minHeight: '80px' }}>
                No screenings yet
              </div>
            ) : (
              screenings.map((screening) => (
                <div key={screening.id} className="dashboard-screening-card">
                  <div
                    className={`dashboard-screening-avatar dashboard-screening-avatar-${screening.color}`}
                  >
                    {screening.initials}
                  </div>
                  <div className="dashboard-screening-info">
                    <div className="dashboard-screening-name">{screening.name}</div>
                    <div className="dashboard-screening-address">{screening.address}</div>
                  </div>
                  <span className="dashboard-screening-grade-badge" style={getGradeStyle(screening.grade)}>
                    Grade: {screening.grade}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
